/*
 * 入口。
 *
 * 顺序有讲究：先挂载 React，加载画面**等会话判定出结果之后**才淡出。
 * 卸载逻辑与它的理由都在 `boot.ts`，这里只负责挂那道硬上限。
 */

import { StrictMode } from "react";
import type { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { SessionProvider, useSession } from "./state/session";
import { MaintenancePage } from "./features/maintenance/MaintenancePage";
import { FloatingToastProvider } from "./components/FloatingToast";
import { MusicPlayerProvider } from "./components/MusicPlayer";
import { router } from "./router";
import { dismissBoot } from "./boot";
import "./styles.css";
import "./components/components.css";

/**
 * 入口维护中就整屏换成维护页。
 *
 * ⚠ **必须在 `RouterProvider` 外面。** 维护态描述的是入口不是这个人，
 * 登录的和没登录的都可能拿到它，而 `/login` 在 `RequireSession` 之外
 * （`router.tsx`）。放进 `RequireSession` 的话未登录的人会被送到登录页，
 * 去登录，而登录同样被服务端挡下——他全程得不到任何解释。
 * 夹在这里是**一处判定覆盖全部路由**，不用在两个地方各写一遍。
 */
function MaintenanceGate({ children }: { children: ReactNode }) {
  const session = useSession();
  return session.status === "maintenance" ? <MaintenancePage /> : <>{children}</>;
}

const container = document.getElementById("root");
if (!container) throw new Error("找不到 #root 挂载点");

createRoot(container).render(
  <StrictMode>
    <SessionProvider>
      <MusicPlayerProvider>
        <FloatingToastProvider>
          <MaintenanceGate>
            <RouterProvider router={router} />
          </MaintenanceGate>
        </FloatingToastProvider>
      </MusicPlayerProvider>
    </SessionProvider>
  </StrictMode>,
);

/*
 * 硬上限。正常路径由 `session.tsx` 在会话判定落地时调 `dismissBoot()`，
 * 这里只防「那条回调因为某个分支没落地而永远不来」——那种情况下用户会被
 * 永久困在加载页，比多看几秒空界面严重得多。
 *
 * 10 秒的依据：`app/session` 走 `apiRequest` 的默认 8 秒超时，超时后 session.tsx
 * 的 catch 会把状态挪到 authenticated + degraded。留 2 秒余量。
 */
window.setTimeout(dismissBoot, 10_000);
